import { clearPortalSentryUser } from '@admin-panel/observability';
import { LOCAL_GATEWAY_AUTH_BYPASS_ENABLED, PORTAL_NAME } from '@admin-panel/config/env';

type HttpErrorLike = {
  response?: {
    status?: unknown;
  };
  status?: unknown;
  statusCode?: unknown;
};

let hasHandledUnauthorizedSession = false;

const toStatusCode = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return undefined;
};

export const getHttpStatusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const maybeError = error as HttpErrorLike;

  return (
    toStatusCode(maybeError.response?.status) ??
    toStatusCode(maybeError.statusCode) ??
    toStatusCode(maybeError.status)
  );
};

export const isUnauthorizedHttpError = (error: unknown) => getHttpStatusCode(error) === 401;

export const resetUnauthorizedSessionHandling = () => {
  hasHandledUnauthorizedSession = false;
};

/**
 * Facts about this device, not the signed-in person, so they survive logout: the device identity
 * the next login request needs, Hub's confirmed alert sound test (a shared phone stays set up),
 * Hub's Local/Stage alert-setup preview mode, Hub's installation id and last setup report
 * (adoption tracking counts devices, not logins), and which operators chose "Ring for all my
 * facilities" on this device.
 */
export const DEVICE_STORAGE_KEYS = [
  'fingerprint',
  'hub-alert-setup-done',
  'hub-alert-setup-mode',
  'hub-alerts-ring-all',
  'hub-install-id',
  'hub-setup-report',
] as const;

export const clearClientStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  clearPortalSentryUser();
  const kept = DEVICE_STORAGE_KEYS.map(key => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  kept.forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
    }
  });
  sessionStorage.clear();
};

// Foundation's STN approval deep link (sent in approval emails), rebuilt from validated parts.
const stnApprovalReturn = (returnTo?: string, search?: string) => {
  if (PORTAL_NAME !== 'admin' || returnTo !== '/stocktransfer' || !search) {
    return undefined;
  }
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (params.get('stockTransferTab') !== 'approval' || !id || !/^[1-9]\d*$/.test(id)) {
    return undefined;
  }
  return `/stocktransfer?stockTransferTab=approval&id=${id}${params.get('showTrial') === '1' ? '&showTrial=1' : ''}`;
};

const RECEIVE_FRAGMENT = /^#w=((?:PACKAGE|RETURN):[1-9]\d{0,15})(?:&c=(\d{6}))?$/;
/** This tab's sessionStorage key for a receive link's code carried through login. */
export const RECEIVE_CODE_KEY = 'lk-receive-code';
/** A code carried longer than this is dropped: the rider shows a fresh one anyway. */
const RECEIVE_CODE_TTL_MS = 30 * 60_000;

/**
 * Hub and Nexus receive links (`/receive#w=TYPE:id&c=code`, the rider's handover QR) keep their
 * parcel through login: the destination is rebuilt from the validated fragment only, WITHOUT the
 * code. The login URL's query reaches servers and their logs; the code must not.
 */
export const receiveDestination = (pathname?: string, hash?: string) => {
  if (!['warehouse', 'vendor'].includes(PORTAL_NAME) || pathname !== '/receive' || !hash) {
    return undefined;
  }
  const match = RECEIVE_FRAGMENT.exec(hash);
  return match ? `/receive#w=${match[1]}` : undefined;
};

/**
 * The code-free login destination for a receive link, with its code kept in this tab's
 * sessionStorage (written after any session clear) for ReceiveLinkPage to read once after login.
 */
export const carryReceiveLink = (pathname?: string, hash?: string) => {
  const destination = receiveDestination(pathname, hash);
  const match = destination ? RECEIVE_FRAGMENT.exec(hash!) : null;
  if (match?.[2] && typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(
        RECEIVE_CODE_KEY,
        JSON.stringify({ w: match[1], c: match[2], at: Date.now() }),
      );
    } catch {
      // Storage blocked: the parcel still opens; the receiver types the code.
    }
  }
  return destination;
};

/** The carried code for exactly this parcel, if still fresh. Does not remove it. */
export const peekReceiveCode = (work: { workType: string; workId: number }) => {
  try {
    const carried = JSON.parse(sessionStorage.getItem(RECEIVE_CODE_KEY) || 'null') as {
      w?: unknown;
      c?: unknown;
      at?: unknown;
    } | null;
    const age = Date.now() - Number(carried?.at);
    return carried?.w === `${work.workType}:${work.workId}` &&
      typeof carried.c === 'string' &&
      /^\d{6}$/.test(carried.c) &&
      age >= 0 &&
      age <= RECEIVE_CODE_TTL_MS
      ? carried.c
      : undefined;
  } catch {
    return undefined;
  }
};

export const clearReceiveCode = () => {
  try {
    sessionStorage.removeItem(RECEIVE_CODE_KEY);
  } catch {
    // Nothing carried.
  }
};

/** Reads the carried code once: any carried code is removed, matching or not. */
export const takeReceiveCode = (work: { workType: string; workId: number }) => {
  const code = peekReceiveCode(work);
  clearReceiveCode();
  return code;
};

/**
 * A Hub screen to come back to after signing in again: a plain in-app path (letters, digits, hyphens; at most five
 * parts), never a query, another site or the login page itself. Round 30: a worker signed out mid-count came back
 * to Home, not to the count.
 */
const hubReturn = (returnTo?: string) =>
  PORTAL_NAME === 'warehouse' &&
  returnTo &&
  returnTo !== '/login' &&
  /^(?:\/[A-Za-z0-9-]{1,64}){1,5}$/.test(returnTo)
    ? returnTo
    : undefined;

export const redirectToLogin = (returnTo?: string, search?: string, hash?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname !== '/login') {
    // Hub screens, Hub/Nexus receive links and Foundation STN approval links keep a destination. Explicit logout
    // passes no path; other redirect targets keep their existing behavior.
    const destination =
      carryReceiveLink(returnTo, hash) ??
      hubReturn(returnTo) ??
      stnApprovalReturn(returnTo, search);
    window.location.replace(`/login${destination ? `?to=${encodeURIComponent(destination)}` : ''}`);
  }
};

/**
 * Hub: set when a 401 signed the worker out without asking (`{ userId, at }`). The login page then says so in plain
 * words, and the next sign-in decides whose work the phone still holds (settleKeptWork).
 */
export const HUB_SIGNED_OUT_KEY = 'hub-signed-out';
/** The sign-in itself. Everything else in storage is the phone's work: the facility, kept counts and scans, ids of
 * requests that may still land. */
const SESSION_KEYS = ['at', 'rt', 'user', 'persist:root'] as const;

const signedInUserId = (): number | undefined => {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null') as { id?: unknown } | null;
    return typeof user?.id === 'number' ? user.id : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Hub's sign-out on a 401. The gateway answers 401 both for a session that ended and for one it could not check (the
 * sign-in service restarting), so the phone cannot tell them apart. Only the sign-in goes: the facility, a count in
 * progress, scans kept for a parcel and the ids of requests sent stay on the phone for the same person (round 30,
 * Kavya: a 10-piece count was lost on a sign-in blip). A different person signing in next starts clean.
 */
const signOutKeepingWork = () => {
  const userId = signedInUserId();
  clearPortalSentryUser();
  SESSION_KEYS.forEach(key => localStorage.removeItem(key));
  try {
    localStorage.setItem(HUB_SIGNED_OUT_KEY, JSON.stringify({ userId, at: Date.now() }));
  } catch {
    // Storage blocked: the login page shows no note; the next sign-in finds no marker and keeps the work as it is.
  }
};

/** Hub: the worker was signed out mid-work (not by Log out); the login page says so. */
export const wasSignedOutMidWork = () => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem(HUB_SIGNED_OUT_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Called with the person who just signed in, before their sign-in is stored. Work kept by a 401 sign-out is theirs
 * only if they are the person who was signed out; anyone else gets a clean phone (device keys stay).
 */
export const settleKeptWork = (userId?: number) => {
  if (typeof window === 'undefined') return;
  const raw = localStorage.getItem(HUB_SIGNED_OUT_KEY);
  if (raw === null) return;
  let keptFor: unknown;
  try {
    keptFor = (JSON.parse(raw) as { userId?: unknown } | null)?.userId;
  } catch {
    keptFor = undefined;
  }
  if (keptFor === undefined || keptFor !== userId) {
    // A rider's receive link opened this sign-in: its code is for whoever receives, not the person signed out.
    const receiveCode = sessionStorage.getItem(RECEIVE_CODE_KEY);
    clearClientStorage();
    if (receiveCode !== null) sessionStorage.setItem(RECEIVE_CODE_KEY, receiveCode);
  }
  localStorage.removeItem(HUB_SIGNED_OUT_KEY);
};

export const handleUnauthorizedSessionExpiry = () => {
  if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
    return;
  }

  if (hasHandledUnauthorizedSession) {
    return;
  }

  hasHandledUnauthorizedSession = true;
  const returnTo = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const search = typeof window !== 'undefined' ? window.location.search : undefined;
  const hash = typeof window !== 'undefined' ? window.location.hash : undefined;
  if (PORTAL_NAME === 'warehouse') signOutKeepingWork();
  else clearClientStorage();
  redirectToLogin(returnTo, search, hash);
};
